// ListNavigationContext.tsx - Contexto para navegação por teclado em listas
// Bigwa - Janeiro 2026

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface ListNavigationState {
  items: string[];
  focusedIndex: number;
  onFocusChange?: (index: number, id: string | null) => void;
  onOpen?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleInspector?: () => void;
  onInspectorTabChange?: (tab: number) => void;
  pageId?: string;
}

interface ListNavigationContextType {
  state: ListNavigationState;
  registerList: (config: Partial<ListNavigationState>) => void;
  unregisterList: () => void;
  focusNext: () => void;
  focusPrev: () => void;
  openFocused: () => void;
  deleteFocused: () => void;
  toggleInspector: () => void;
  setInspectorTab: (tab: number) => void;
  setFocusedIndex: (index: number) => void;
  focusedId: string | null;
}

const defaultState: ListNavigationState = {
  items: [],
  focusedIndex: -1,
};

const ListNavigationContext = createContext<ListNavigationContextType | null>(null);

export function ListNavigationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ListNavigationState>(defaultState);

  const focusedId = state.focusedIndex >= 0 && state.focusedIndex < state.items.length 
    ? state.items[state.focusedIndex] 
    : null;

  const registerList = useCallback((config: Partial<ListNavigationState>) => {
    setState(prev => {
      const newItems = config.items || prev.items;
      const newIndex = newItems.length > 0 ? 0 : -1;
      return { ...prev, ...config, focusedIndex: newIndex };
    });
  }, []);

  const unregisterList = useCallback(() => {
    setState(defaultState);
  }, []);

  const setFocusedIndex = useCallback((index: number) => {
    setState(prev => {
      const newIndex = Math.max(-1, Math.min(index, prev.items.length - 1));
      const newId = newIndex >= 0 ? prev.items[newIndex] : null;
      setTimeout(() => prev.onFocusChange?.(newIndex, newId), 0);
      return { ...prev, focusedIndex: newIndex };
    });
  }, []);

  const focusNext = useCallback(() => {
    setState(prev => {
      if (prev.items.length === 0) return prev;
      const newIndex = Math.min(prev.focusedIndex + 1, prev.items.length - 1);
      const newId = prev.items[newIndex];
      setTimeout(() => prev.onFocusChange?.(newIndex, newId), 0);
      return { ...prev, focusedIndex: newIndex };
    });
  }, []);

  const focusPrev = useCallback(() => {
    setState(prev => {
      if (prev.items.length === 0) return prev;
      const newIndex = Math.max(prev.focusedIndex - 1, 0);
      const newId = prev.items[newIndex];
      setTimeout(() => prev.onFocusChange?.(newIndex, newId), 0);
      return { ...prev, focusedIndex: newIndex };
    });
  }, []);

  const openFocused = useCallback(() => {
    setState(prev => {
      if (prev.focusedIndex >= 0 && prev.focusedIndex < prev.items.length) {
        const id = prev.items[prev.focusedIndex];
        setTimeout(() => prev.onOpen?.(id), 0);
      }
      return prev;
    });
  }, []);

  const deleteFocused = useCallback(() => {
    setState(prev => {
      if (prev.focusedIndex >= 0 && prev.focusedIndex < prev.items.length) {
        const id = prev.items[prev.focusedIndex];
        setTimeout(() => prev.onDelete?.(id), 0);
      }
      return prev;
    });
  }, []);

  const toggleInspector = useCallback(() => {
    setState(prev => {
      setTimeout(() => prev.onToggleInspector?.(), 0);
      return prev;
    });
  }, []);

  const setInspectorTab = useCallback((tab: number) => {
    setState(prev => {
      setTimeout(() => prev.onInspectorTabChange?.(tab), 0);
      return prev;
    });
  }, []);

  return (
    <ListNavigationContext.Provider
      value={{
        state, registerList, unregisterList, focusNext, focusPrev,
        openFocused, deleteFocused, toggleInspector, setInspectorTab,
        setFocusedIndex, focusedId,
      }}
    >
      {children}
    </ListNavigationContext.Provider>
  );
}

export function useListNavigation() {
  const context = useContext(ListNavigationContext);
  if (!context) {
    return {
      state: defaultState,
      registerList: () => {},
      unregisterList: () => {},
      focusNext: () => {},
      focusPrev: () => {},
      openFocused: () => {},
      deleteFocused: () => {},
      toggleInspector: () => {},
      setInspectorTab: () => {},
      setFocusedIndex: () => {},
      focusedId: null,
    };
  }
  return context;
}

export function useRegisterList(config: {
  items: string[];
  pageId: string;
  onFocusChange?: (index: number, id: string | null) => void;
  onOpen?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleInspector?: () => void;
  onInspectorTabChange?: (tab: number) => void;
}) {
  const { registerList, unregisterList, state, setFocusedIndex, focusedId } = useListNavigation();
  const itemsKey = config.items.join(',');
  
  useEffect(() => {
    registerList({
      items: config.items,
      pageId: config.pageId,
      onFocusChange: config.onFocusChange,
      onOpen: config.onOpen,
      onDelete: config.onDelete,
      onToggleInspector: config.onToggleInspector,
      onInspectorTabChange: config.onInspectorTabChange,
    });
    return () => unregisterList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, config.pageId]);
  
  return { focusedIndex: state.focusedIndex, focusedId, setFocusedIndex };
}
